import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, SERVICES } from "@/lib/rn3";
import { toast } from "sonner";
import { Calendar, Check, ChevronLeft, ChevronRight, Clock } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

import { useT, useLang } from "@/lib/i18n";
import SEO from "@/components/SEO";

const Appointments = () => {
  const tt = useT();
  const { lang } = useLang();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const presetService = params.get("service") || "consultation";

  const [form, setForm] = useState({
    service: presetService,
    full_name: "",
    phone: "",
    email: "",
    date: todayISO(),
    time: "",
    notes: "",
    preferred_language: "en",
  });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  // generate next 14 days
  const days = useMemo(() => {
    const arr = [];
    const t = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(t); d.setDate(t.getDate() + i);
      arr.push(d.toISOString().slice(0, 10));
    }
    return arr;
  }, []);

  useEffect(() => {
    setLoadingSlots(true);
    api.get(`/appointments/available-slots?date=${form.date}`)
      .then(({ data }) => setSlots(data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [form.date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.time) { toast.error(tt("appts.select_time")); return; }
    try {
      const { data } = await api.post("/appointments", form);
      setSubmitted(data);
      toast.success(tt("appts.toast_ok"));
    } catch (err) {
      toast.error(err.response?.data?.detail || tt("appts.toast_fail"));
    }
  };

  if (submitted) {
    return (
      <div className="bg-[#FDFBF7] min-h-[60vh]" data-testid="appt-confirmation">
        <div className="rn3-container py-24 text-center">
          <Check className="w-12 h-12 text-[#C4A252] mx-auto" />
          <h1 className="font-serif-display text-4xl mt-4">{tt("appts.requested")}</h1>
          <div className="border-t border-[#C4A252] w-12 mx-auto my-5" />
          <p className="text-[#404040] max-w-md mx-auto">
            {tt("appts.requested_body_a")} <b>{submitted.service.replace(/_/g, " ")}</b> {tt("appts.requested_body_b")}{" "}
            <b>{submitted.date}</b> {tt("appts.requested_body_c")} <b>{submitted.time}</b>. {tt("appts.requested_body_d")}
          </p>
          <button onClick={() => setSubmitted(null)} className="mt-8 rn3-btn-gold" data-testid="book-another-btn">{tt("appts.book_another")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <SEO
        title="Book an Appointment — Roman Numerals III | Jacksonville, FL"
        description="Schedule a consultation with Roman Numerals III for bookkeeping, tax preparation, notary, or ITIN services. Bilingual (English/Spanish) appointments online."
        path="/appointments"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Appointments", path: "/appointments" }]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ReserveAction",
          "target": "https://romannumeralsiii.com/appointments",
          "result": { "@type": "Reservation", "provider": { "@id": "https://romannumeralsiii.com/#organization" } }
        }}
      />
      <section className="bg-[#FDFBF7]">
        <div className="rn3-container py-16">
          <div className="rn3-eyebrow">{tt("appts.eyebrow")}</div>
          <h1 className="font-serif-display text-5xl mt-4 leading-tight">
            {tt("appts.h1_a")} <span className="italic text-[#8B1C22]">{tt("appts.h1_b")}</span>
          </h1>
          <p className="mt-4 max-w-xl text-[#404040]">{tt("appts.intro")}</p>
        </div>
      </section>

      <section className="rn3-section">
        <div className="rn3-container grid lg:grid-cols-12 gap-12">
          <form onSubmit={submit} className="lg:col-span-7 space-y-6" data-testid="appt-form">
            <div>
              <label className="rn3-eyebrow block mb-2">{tt("appts.form.service")}</label>
              <select
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className="rn3-input"
                data-testid="appt-service"
              >
                <option value="consultation">{tt("appts.consultation")}</option>
                {SERVICES.map((s) => <option key={s.key} value={s.key}>{tt(`service.${s.key}`, s.title)}</option>)}
              </select>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="rn3-eyebrow block mb-2">{tt("appts.full_name")}</label>
                <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="rn3-input" data-testid="appt-name" />
              </div>
              <div>
                <label className="rn3-eyebrow block mb-2">{tt("appts.phone")}</label>
                <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rn3-input" data-testid="appt-phone" />
              </div>
              <div className="sm:col-span-2">
                <label className="rn3-eyebrow block mb-2">{tt("appts.email")}</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rn3-input" data-testid="appt-email" />
              </div>
              <div className="sm:col-span-2">
                <label className="rn3-eyebrow block mb-2">{tt("appts.lang_label")}</label>
                <select value={form.preferred_language} onChange={(e) => setForm({ ...form, preferred_language: e.target.value })} className="rn3-input" data-testid="appt-lang">
                  <option value="en">{tt("common.english")}</option>
                  <option value="es">{tt("common.spanish")}</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="rn3-eyebrow block mb-2">{tt("appts.notes_optional")}</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rn3-input" rows="3" data-testid="appt-notes" />
              </div>
            </div>

            <button type="submit" className="rn3-btn-primary" data-testid="appt-submit">
              {tt("appts.form.submit")}
            </button>
          </form>

          <aside className="lg:col-span-5">
            <div className="rn3-card">
              <div className="flex items-center gap-2 rn3-eyebrow"><Calendar className="w-4 h-4" /> {tt("appts.choose_date")}</div>
              <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar pb-2" data-testid="appt-day-picker">
                {days.map((d) => {
                  const dt = new Date(d + "T00:00:00");
                  const active = d === form.date;
                  return (
                    <button
                      key={d}
                      onClick={() => setForm({ ...form, date: d, time: "" })}
                      type="button"
                      data-testid={`day-${d}`}
                      className={`shrink-0 w-16 py-3 text-center border transition-colors ${
                        active ? "bg-[#8B1C22] text-white border-[#8B1C22]" : "border-[#E5E5E5] hover:border-[#C4A252]"
                      }`}
                    >
                      <div className="text-[10px] uppercase tracking-widest">{dt.toLocaleDateString(lang === "es" ? "es" : "en", { weekday: "short" })}</div>
                      <div className="font-serif-display text-2xl mt-1">{dt.getDate()}</div>
                      <div className="text-[10px]">{dt.toLocaleDateString(lang === "es" ? "es" : "en", { month: "short" })}</div>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-2 rn3-eyebrow mt-6"><Clock className="w-4 h-4" /> {tt("appts.available_times")}</div>
              {loadingSlots ? (
                <div className="text-sm text-[#737373] mt-4">{tt("common.loading")}</div>
              ) : slots.length === 0 ? (
                <div className="text-sm text-[#737373] mt-4">{tt("appts.no_slots")}</div>
              ) : (
                <div className="mt-4 grid grid-cols-3 gap-2" data-testid="appt-time-grid">
                  {slots.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setForm({ ...form, time: t })}
                      data-testid={`slot-${t}`}
                      className={`py-2 text-sm border transition-colors ${
                        form.time === t ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:border-[#C4A252]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-[#737373] mt-5">{tt("appts.calendar_soon")}</p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Appointments;
